"use client";

import { useRef } from "react";
import { cn } from "@/lib/utils";
import { useImageUpload } from "@/app/hooks/images/use-image-upload";
import { useDragDrop } from "@/app/hooks/images/use-drag-drop";
import { ImagePreview } from "./image-upload/image-preview";
import { ImageMetadata } from "./image-upload/image-metadata";
import { DragOverlay } from "./image-upload/drag-overlay";
import { UploadActionButtons } from "./image-upload/upload-action-buttons";
import ImagePreviewLink from "./image-upload/image-preview-link";
import { BackendLabel } from "./backend-label";
import type { ImageTypeT, ImageEntityTypeT } from "@/app/types/images";

export type BackendImageUploadProps = {
  label: string;
  currentImageUrl?: string | null;
  currentAltText?: string | null;
  onImageChange: (image: { url: string; alt: string } | null) => void;
  entityType: ImageEntityTypeT;
  entityId: string;
  imageType: ImageTypeT;
  className?: string;
};

/**
 * BackendImageUpload - Reusable component architecture with entity-agnostic design
 *
 * Separation of concerns:
 * - Entity-agnostic image upload/delete operations via useImageUpload hook
 * - Drag & drop functionality via useDragDrop hook
 * - UI components: ImagePreview, ImageMetadata, DragOverlay, UploadActionButtons
 * - File validation via centralized utils
 * - Supports posts, contact_persons, and other entities through configuration
 *
 * @example
 * ```tsx
 * // For posts
 * <BackendImageUpload
 *   label="Header-Bild"
 *   entityType="posts"
 *   entityId={post.id}
 *   imageType="header"
 *   currentImageUrl={formData.headerImage?.url}
 *   onImageChange={handleImageChange}
 * />
 *
 * // For contact persons
 * <BackendImageUpload
 *   label="Profilbild"
 *   entityType="contact_persons"
 *   entityId={contactPerson.id}
 *   imageType="profile"
 *   currentImageUrl={formData.image?.url}
 *   onImageChange={handleImageChange}
 * />
 * ```
 */
export function BackendImageUpload({
  label,
  currentImageUrl,
  currentAltText,
  onImageChange,
  entityType,
  entityId,
  imageType,
  className,
}: BackendImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Custom hooks for clean separation of concerns
  const { isUploading, isDeleting, handleFileUpload, handleImageDelete } =
    useImageUpload({
      entityType,
      entityId,
      imageType,
      currentAltText,
      onImageChange,
    });

  // Wrapper to convert File | File[] to File for useDragDrop compatibility
  const handleFileSelect = (file: File | File[]) => {
    const fileToUpload = Array.isArray(file) ? file[0] : file;
    if (fileToUpload) {
      handleFileUpload(fileToUpload);
    }
  };

  const { isDragOver, dragHandlers } = useDragDrop({
    onFileSelect: handleFileSelect,
    disabled: isUploading,
  });

  // Handle file input change
  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Trigger file input
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Handle container click (avoid triggering on child clicks)
  const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Don't trigger if clicking on the ArrowUpRight link
    if ((event.target as HTMLElement).closest("a")) {
      return;
    }
    triggerFileInput();
  };

  return (
    <div className={cn("flex flex-col gap-1 sm:gap-1.5", className)}>
      <BackendLabel>{label}</BackendLabel>
      <div
        className={cn(
          "relative h-[90px] max-w-md cursor-pointer space-y-4 rounded-md border bg-[var(--c-card)] shadow-xs transition-colors hover:border-gray-300",
          isDragOver && "bg-secondary",
          isUploading && "pointer-events-none opacity-75"
        )}
        {...dragHandlers}
        onClick={handleContainerClick}
      >
        {/* Preview Link - only show if image is uploaded */}
        {currentImageUrl && (
          <ImagePreviewLink currentImageUrl={currentImageUrl} />
        )}
        {/* Image Preview Container */}
        <div className="h-full space-y-2">
          <div className="flex h-full w-full items-start justify-start gap-4">
            <ImagePreview
              imageUrl={currentImageUrl}
              altText={currentAltText}
              label={label}
            />
            <ImageMetadata
              isUploading={isUploading}
              currentImageUrl={currentImageUrl}
              imageType={imageType}
            />
          </div>
        </div>
        {/* Drag Overlay - show if image is dragged over the container */}
        {isDragOver && <DragOverlay />}
      </div>
      {/* Upload Action Buttons - Ersetzen & Löschen */}
      <UploadActionButtons
        isUploading={isUploading}
        isDeleting={isDeleting}
        hasImage={!!currentImageUrl}
        onUploadClick={triggerFileInput}
        onDeleteClick={handleImageDelete}
      />

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
}
